import React, { useState, useCallback, useRef } from 'react';

// ICD-10 data loaded lazily from bundled JSON
let icd10Cache = null;

async function loadICD10() {
  if (icd10Cache) return icd10Cache;
  try {
    const mod = await import('../data/icd10.json');
    icd10Cache = mod.default || mod;
    return icd10Cache;
  } catch {
    return [];
  }
}

export default function ICD10Search({ value = [], onChange, label = 'Diagnosis (ICD-10)' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!q || q.length < 2) { setResults([]); return; }
      setLoading(true);
      const data = await loadICD10();
      const q2 = q.toLowerCase();
      const matches = data.filter(d =>
        d.code.toLowerCase().startsWith(q2) || d.description.toLowerCase().includes(q2)
      ).slice(0, 20);
      setResults(matches);
      setLoading(false);
    }, 250);
  }, []);

  const addDiagnosis = (item) => {
    if (!value.find(v => v.code === item.code)) {
      onChange([...value, { code: item.code, description: item.description, type: 'Primary' }]);
    }
    setQuery('');
    setResults([]);
  };

  const removeItem = (code) => onChange(value.filter(v => v.code !== code));

  const toggleType = (code) => onChange(value.map(v =>
    v.code === code ? { ...v, type: v.type === 'Primary' ? 'Secondary' : 'Primary' } : v
  ));

  return (
    <div>
      {label && <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>{label}</label>}

      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {value.map(d => (
            <div key={d.code} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              backgroundColor: d.type === 'Primary' ? '#eff6ff' : '#f9fafb',
              border: `1px solid ${d.type === 'Primary' ? '#3b82f6' : '#e5e7eb'}`,
              borderRadius: 20, fontSize: 13
            }}>
              <button type="button" onClick={() => toggleType(d.code)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  fontSize: 11, fontWeight: 700, color: d.type === 'Primary' ? '#1d4ed8' : '#6b7280' }}>
                {d.type === 'Primary' ? '1°' : '2°'}
              </button>
              <span style={{ fontWeight: 600, color: '#1e40af' }}>{d.code}</span>
              <span style={{ color: '#374151' }}>{d.description.length > 40 ? d.description.slice(0, 40) + '…' : d.description}</span>
              <button type="button" onClick={() => removeItem(d.code)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative' }}>
        <input
          type="text" value={query}
          onChange={e => { setQuery(e.target.value); search(e.target.value); }}
          placeholder="Search ICD-10 code or description..."
          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
        />
        {loading && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 12 }}>
            Searching...
          </div>
        )}
        {results.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
            border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000, maxHeight: 280, overflowY: 'auto'
          }}>
            {results.map(r => (
              <div key={r.code} onClick={() => addDiagnosis(r)}
                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 10 }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f9ff'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
              >
                <span style={{ fontWeight: 700, color: '#1e40af', minWidth: 60, fontSize: 13 }}>{r.code}</span>
                <span style={{ color: '#374151', fontSize: 13 }}>{r.description}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
